import { useCallback } from 'react';

import { largeUploadWithS3InfoByIdSelector, type Upload } from '@air/redux-uploader';
import { useUploadLargeFileChunkWithRetries } from '@air/upload-utils';

import { type UploadTaskParams } from '~/components/Upload/hooks/types';
import { useLargeUploadPartTrackingHandlers } from '~/components/Upload/hooks/useLargeUploadPartTrackingHandlers';
import { getContentCollectionUploadAnalyticsParams } from '~/components/Upload/utils/uploadAnalytics';
import { metadataForPublicUploadSelector } from '~/store/publicUploadMetadata/selectors';
import { useAirStore } from '~/utils/ReduxUtils';
import { fetchPublicPartUploadUrl } from '~/utils/uploadApi/publicUploadApi/publicLargeUploadApi';

export const useUploadLargePublicFileWithRetries = () => {
  const store = useAirStore();
  const { uploadLargeFileChunkWithRetries } = useUploadLargeFileChunkWithRetries();
  const { getPartTrackingHandlers } = useLargeUploadPartTrackingHandlers();

  const uploadLargePublicFileWithRetries = useCallback(
    ({
      uploadId,
      chunk,
      onError,
      partNumber,
    }: {
      uploadId: Upload['id'];
      partNumber: number;
      chunk: Blob;
      onError: UploadTaskParams['onError'];
    }) => {
      const upload = largeUploadWithS3InfoByIdSelector(store.getState(), uploadId);

      if (upload) {
        return uploadLargeFileChunkWithRetries({
          chunk,
          upload,
          partNumber,
          ...getPartTrackingHandlers({
            getAnalyticsParams: (upload) =>
              getContentCollectionUploadAnalyticsParams({
                context: 'content_collection_large_part_upload',
                state: store.getState(),
                upload,
              }),
            onError,
          }),
          getPartUrl: async (partNumber) => {
            const uploadMetadata = metadataForPublicUploadSelector(store.getState(), upload.id);

            return fetchPublicPartUploadUrl({
              partNumber,
              s3Info: upload.s3Info.uploadUrlInfo,
              shortBoardId: uploadMetadata.shortBoardId,
            });
          },
        });
      }
    },
    [getPartTrackingHandlers, store, uploadLargeFileChunkWithRetries],
  );

  return {
    uploadLargePublicFileWithRetries,
  };
};
